(() => {
  if (window.__cgmContractGateLoaded) return;
  window.__cgmContractGateLoaded = true;

  const VERSION = 'CGM-CGV-2026-10-13';
  const ATTRIBUTE = 'CGM - Accettazione CGV';
  const TIMESTAMP_ATTRIBUTE = 'CGM - Accettazione CGV Timestamp';
  const CONTRACT_ATTRIBUTE = 'CGM - Contratto CGV';
  const CONTRACT_URL = '/pages/condizioni-generali-vendita-contratto-acquisto';
  const COMPLETE_KIT_PRODUCT_TYPE = 'kit fotovoltaico completo';
  const CONSENT_ERROR = 'Per procedere devi leggere e accettare le Condizioni generali di vendita e il Contratto di acquisto.';
  const KIT_LIMIT_ERROR = 'È possibile acquistare un solo kit fotovoltaico completo per ordine. Per quantità superiori richiedere un preventivo di trasporto.';

  let accepted = false;
  let acceptedAt = '';
  let completeKitQuantity = 0;
  let cartLoaded = false;
  let cartFresh = false;
  let updating = false;
  let reviewingCheckout = false;
  let refreshTimer = null;
  let acceptanceUpdate = null;
  let stateRevision = 0;
  const cartUpdates = new Set();

  function normalized(value) {
    return String(value || '').trim().toLocaleLowerCase('it-IT');
  }

  function countCompleteKits(cart) {
    if (!Array.isArray(cart?.items)) return 0;

    return cart.items.reduce((total, item) => {
      const isCompleteKit = normalized(item?.product_type) === COMPLETE_KIT_PRODUCT_TYPE;
      return isCompleteKit ? total + Number(item?.quantity || 0) : total;
    }, 0);
  }

  function readAcceptance(cart) {
    const versionMatches = cart?.attributes?.[ATTRIBUTE] === VERSION;
    const storedTimestamp = cart?.attributes?.[TIMESTAMP_ATTRIBUTE] || '';
    const contractMatches = cart?.attributes?.[CONTRACT_ATTRIBUTE] === CONTRACT_URL;

    accepted = Boolean(versionMatches && storedTimestamp && contractMatches);
    acceptedAt = accepted ? storedTimestamp : '';
  }

  async function loadCartState() {
    if (acceptanceUpdate) await acceptanceUpdate;
    if (cartUpdates.size) await Promise.all([...cartUpdates]);
    const revision = stateRevision;
    cartFresh = false;
    applyGate();
    let loadedSuccessfully = false;

    try {
      const response = await fetch('/cart.js', {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Cart load failed');

      const cart = await response.json();
      if (revision !== stateRevision) return;
      readAcceptance(cart);
      completeKitQuantity = countCompleteKits(cart);
      loadedSuccessfully = true;
    } catch (_) {
      if (revision !== stateRevision) return;
      accepted = false;
      acceptedAt = '';
    } finally {
      if (revision !== stateRevision) return;
      cartLoaded = true;
      cartFresh = loadedSuccessfully;
      applyGate();
    }
  }

  async function saveAcceptance(isAccepted) {
    if (acceptanceUpdate) await acceptanceUpdate;
    stateRevision += 1;
    updating = true;
    accepted = Boolean(isAccepted);
    acceptedAt = accepted ? new Date().toISOString() : '';
    applyGate();

    try {
      const attributes = {};
      attributes[ATTRIBUTE] = accepted ? VERSION : '';
      attributes[TIMESTAMP_ATTRIBUTE] = acceptedAt;
      attributes[CONTRACT_ATTRIBUTE] = accepted ? CONTRACT_URL : '';

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes })
      });
      if (!response.ok) throw new Error('Cart attribute update failed');

      const cart = await response.json();
      readAcceptance(cart);
      completeKitQuantity = countCompleteKits(cart);
      cartLoaded = true;
      cartFresh = true;
    } catch (_) {
      accepted = false;
      acceptedAt = '';
    } finally {
      updating = false;
      applyGate();
    }
  }

  function checkoutAllowed() {
    const ivaOk = !window.cgmIva || window.cgmIva.ok();
    return cartLoaded && cartFresh && accepted && ivaOk && !updating && cartUpdates.size === 0 && completeKitQuantity <= 1;
  }

  function ensureStyles() {
    if (document.getElementById('cgm-contract-gate-styles')) return;

    const style = document.createElement('style');
    style.id = 'cgm-contract-gate-styles';
    style.textContent = `
      .cgm-contract-gate__kit-error {
        margin: 0;
        padding: var(--padding-md);
        border: 1px solid currentColor;
        border-radius: var(--style-border-radius-inputs);
        font-size: var(--font-size--xs);
        font-weight: 600;
        line-height: 1.45;
      }
      .cgm-contract-gate__kit-error[hidden] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  function ensureKitError(gate, checkbox, button) {
    let error = gate.querySelector('.cgm-contract-gate__kit-error');
    if (error) return error;

    error = document.createElement('p');
    error.className = 'cgm-contract-gate__kit-error';
    error.id = `${checkbox.id || 'cgm-contract'}-kit-error`;
    error.setAttribute('role', 'alert');
    error.setAttribute('aria-live', 'assertive');
    error.tabIndex = -1;
    error.hidden = true;
    error.textContent = KIT_LIMIT_ERROR;
    gate.insertBefore(error, button);
    return error;
  }

  function applyGate() {
    ensureStyles();

    document.querySelectorAll('.cgm-contract-gate').forEach((gate) => {
      const checkbox = gate.querySelector('.cgm-contract-gate__checkbox');
      const button = gate.querySelector('.cart__checkout-button');
      if (!checkbox || !button) return;

      const kitError = ensureKitError(gate, checkbox, button);
      const kitLimitExceeded = completeKitQuantity > 1;

      gate.dataset.cgmCompleteKitQuantity = String(completeKitQuantity);
      gate.dataset.cgmKitLimitExceeded = kitLimitExceeded ? 'true' : 'false';
      kitError.hidden = !kitLimitExceeded;

      if (checkbox.value !== VERSION) checkbox.value = VERSION;
      checkbox.required = true;
      checkbox.disabled = updating;
      const shouldCheck = cartLoaded && accepted;
      if (checkbox.checked !== shouldCheck) checkbox.checked = shouldCheck;
      checkbox.setAttribute('aria-describedby', `${checkbox.id}-hint`);

      if (kitLimitExceeded) {
        button.setAttribute('aria-describedby', kitError.id);
      } else if (button.getAttribute('aria-describedby') === kitError.id) {
        button.removeAttribute('aria-describedby');
      }

      // CGM owns the checkout disabled state. Horizon can render the button
      // disabled during hydration; caching that transient state permanently
      // prevented checkout even after the customer accepted the contract.
      const shouldDisable = !checkoutAllowed();
      if (button.disabled !== shouldDisable) button.disabled = shouldDisable;
      delete button.dataset.cgmBaseDisabled;

      if (!checkbox.dataset.cgmBound) {
        checkbox.dataset.cgmBound = 'true';
        checkbox.addEventListener('change', () => {
          checkbox.setCustomValidity('');
          acceptanceUpdate = saveAcceptance(checkbox.checked).finally(() => {
            acceptanceUpdate = null;
          });
        });
      }
    });
  }

  function visibleGate() {
    const gates = Array.from(document.querySelectorAll('.cgm-contract-gate'));
    return gates.find((gate) => gate.getClientRects().length > 0) || gates[0] || null;
  }

  function showCheckoutError() {
    const gate = visibleGate();
    if (!gate) return;

    const checkbox = gate.querySelector('.cgm-contract-gate__checkbox');
    const button = gate.querySelector('.cart__checkout-button');
    if (!checkbox || !button) return;

    if (window.cgmIva && !window.cgmIva.ok()) {
      window.cgmIva.showError();
      return;
    }

    if (completeKitQuantity > 1) {
      const kitError = ensureKitError(gate, checkbox, button);
      kitError.hidden = false;
      kitError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      kitError.focus({ preventScroll: true });
      return;
    }

    checkbox.setCustomValidity(CONSENT_ERROR);
    checkbox.reportValidity();
    checkbox.focus({ preventScroll: false });
    window.setTimeout(() => checkbox.setCustomValidity(''), 100);
  }

  function isCheckoutLink(element) {
    if (!element) return false;
    if (element.matches('button[name="checkout"], input[name="checkout"]')) return true;

    if (element.matches('a[href]')) {
      try {
        const url = new URL(element.href, window.location.origin);
        return (
          url.origin === window.location.origin &&
          (/^\/checkout(?:\/|$)/.test(url.pathname) || /^\/checkouts(?:\/|$)/.test(url.pathname))
        );
      } catch (_) {
        return false;
      }
    }

    return false;
  }

  function checkoutDestination(element) {
    if (element?.matches('a[href]')) return element.href;
    return '/checkout';
  }

  async function reviewCheckout(destination) {
    if (reviewingCheckout) return;
    reviewingCheckout = true;

    try {
      window.clearTimeout(refreshTimer);
      if (acceptanceUpdate) await acceptanceUpdate;
      await loadCartState();
      if (window.cgmIva) await window.cgmIva.refresh();

      if (checkoutAllowed()) {
        window.location.assign(destination || '/checkout');
        return;
      }

      showCheckoutError();
    } finally {
      reviewingCheckout = false;
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const eventTarget = event.target instanceof Element ? event.target : event.target?.parentElement;
      const target = eventTarget?.closest('a[href], button[name="checkout"], input[name="checkout"]');
      if (!isCheckoutLink(target)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      reviewCheckout(checkoutDestination(target));
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      const isCartForm = event.target?.id === 'cart-form';
      if (!(isCheckoutLink(submitter) || (isCartForm && !submitter))) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      reviewCheckout(checkoutDestination(submitter));
    },
    true
  );

  function nodeTouchesCart(node) {
    if (!(node instanceof Element)) return false;
    if (node.closest('.cgm-contract-gate, [data-pp-message], .paypal-messages, iframe')) return false;
    const selector = '.cart-items, .cart-items__table, .cart-items__table-row';
    return node.matches('cart-items-component') || node.matches(selector) || Boolean(node.closest(selector)) || Boolean(node.querySelector(selector));
  }

  function mutationTouchesCart(mutation) {
    if (nodeTouchesCart(mutation.target)) return true;
    return [...mutation.addedNodes, ...mutation.removedNodes].some(nodeTouchesCart);
  }

  function scheduleCartRefresh() {
    if (!cartLoaded || updating || reviewingCheckout || cartUpdates.size) return;

    cartFresh = false;
    applyGate();
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(loadCartState, 450);
  }

  const observer = new MutationObserver((mutations) => {
    applyGate();
    if (mutations.some(mutationTouchesCart)) scheduleCartRefresh();
  });

  const start = () => {
    applyGate();
    observer.observe(document.documentElement, { childList: true, subtree: true });
    loadCartState();
  };

  window.addEventListener('pageshow', () => {
    if (cartLoaded) loadCartState();
  });
  document.addEventListener('cart:update', scheduleCartRefresh);
  document.addEventListener('cart:refresh', scheduleCartRefresh);
  // Horizon emits this event before its cart request completes. Refresh only
  // after its promise settles, so removing a second kit also releases the gate.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (!event.promise) {
      scheduleCartRefresh();
      return;
    }
    stateRevision += 1;
    cartFresh = false;
    applyGate();
    const pending = Promise.resolve(event.promise).catch(() => {}).finally(() => {
      cartUpdates.delete(pending);
      if (!cartUpdates.size) loadCartState();
    });
    cartUpdates.add(pending);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
